import React, { useState } from 'react';
import {
  CircleStackIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface BackupData {
  version: string;
  timestamp: string;
  tables: {
    [tableName: string]: any[];
  };
  metadata: {
    totalRecords: number;
    includedTables: string[];
    exportedBy?: string;
  };
}

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onClose?: () => void;
  buttons?: {
    cancel: { text: string; onPress: () => void };
    confirm: { text: string; onPress: () => void };
  };
  button?: { text: string; onPress: () => void };
}

interface DatabaseBackupProps {
  onClose?: () => void;
}

const DatabaseBackup: React.FC<DatabaseBackupProps> = ({ onClose }) => {
  const [activeModal, setActiveModal] = useState<'export' | 'import' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [keepMembers, setKeepMembers] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [backupData, setBackupData] = useState<BackupData | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  // Export database
  const handleExport = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/backup/export', {
        responseType: 'json'
      });
      
      if (response.data.success) {
        const backupData = response.data.data;
        
        // Create downloadable JSON file
        const jsonString = JSON.stringify(backupData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        link.href = url;
        link.download = `database-backup-${timestamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setActiveModal(null);
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Backup erfolgreich',
          message: `Datenbank-Backup wurde erstellt und heruntergeladen.\n${backupData.metadata.totalRecords} Datensätze aus ${backupData.metadata.includedTables.length} Tabellen exportiert.`,
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) }
        });
      }
    } catch (error: any) {
      console.error('Export error:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler beim Export',
        message: error.response?.data?.message || 'Die Datenbank konnte nicht exportiert werden.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Read and validate file
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          
          // Validate backup structure
          if (!data.version || !data.timestamp || !data.tables || !data.metadata) {
            throw new Error('Ungültiges Backup-Format');
          }
          
          setBackupData(data);
        } catch (error) {
          setConfirmModal({
            visible: true,
            type: 'error',
            title: 'Ungültige Datei',
            message: 'Die ausgewählte Datei ist kein gültiges Datenbank-Backup.',
            variant: 'single',
            button: { text: 'OK', onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) }
          });
          setSelectedFile(null);
          setBackupData(null);
        }
      };
      reader.readAsText(file);
    }
  };

  // Export database as SQL dump
  const handleSqlExport = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/backup/export-sql', {
        responseType: 'blob'
      });

      // Create downloadable SQL file
      const blob = new Blob([response.data], { type: 'application/sql' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.href = url;
      link.download = `database-backup-full-${timestamp}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Vollständiger SQL-Dump erstellt',
        message: 'Die gesamte MySQL-Datenbank wurde exportiert: alle Tabellen und Daten sowie vorhandene Views, Trigger, Routinen und Events. Die SQL-Datei kann direkt mit dem MySQL-Client importiert werden.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) }
      });
    } catch (error: any) {
      console.error('SQL export error:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler beim SQL-Export',
        message: error.response?.data?.message || 'Der SQL-Dump konnte nicht erstellt werden.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Import database
  const handleImport = async () => {
    if (!backupData) return;
    
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Import bestätigen',
      message: `WARNUNG: Diese Aktion wird ${keepMembers ? 'die Datenbank (außer Mitglieder)' : 'die gesamte Datenbank'} überschreiben!\n\nBackup vom: ${new Date(backupData.timestamp).toLocaleString('de-DE')}\nTabellen: ${backupData.metadata.includedTables.length}\nDatensätze: ${backupData.metadata.totalRecords}\n\nMöchten Sie wirklich fortfahren?`,
      variant: 'two',
      buttons: {
        cancel: { 
          text: 'Abbrechen', 
          onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) 
        },
        confirm: { 
          text: 'Importieren', 
          onPress: async () => {
            setIsLoading(true);
            try {
              const response = await apiClient.post('/api/backup/import', {
                backup: backupData,
                options: {
                  keepMembers: keepMembers,
                  resetAutoIncrement: true
                }
              });
              
              if (response.data.success) {
                setActiveModal(null);
                setSelectedFile(null);
                setBackupData(null);
                setConfirmModal({
                  visible: true,
                  type: 'success',
                  title: 'Import erfolgreich',
                  message: `Datenbank-Backup wurde erfolgreich importiert.\n${response.data.data.importedRecords} Datensätze importiert.`,
                  variant: 'single',
                  button: { text: 'OK', onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) }
                });
              }
            } catch (error: any) {
              console.error('Import error:', error);
              setConfirmModal({
                visible: true,
                type: 'error',
                title: 'Fehler beim Import',
                message: error.response?.data?.message || 'Das Backup konnte nicht importiert werden.',
                variant: 'single',
                button: { text: 'OK', onPress: () => setConfirmModal((prev: ConfirmModalState) => ({ ...prev, visible: false })) }
              });
            } finally {
              setIsLoading(false);
            }
          }
        }
      }
    });
  };

  return (
    <>
      {/* Main buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => setActiveModal('export')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all font-medium flex items-center justify-center gap-2 shadow-sm"
        >
          <DocumentArrowDownIcon className="h-5 w-5" />
          JSON exportieren
        </button>
        <button
          onClick={handleSqlExport}
          disabled={isLoading}
          className="px-4 py-2 bg-info-600 text-white rounded-lg hover:bg-info-700 transition-all font-medium flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
        >
          <CircleStackIcon className="h-5 w-5" />
          Vollständiger SQL-Dump
        </button>
        <button
          onClick={() => setActiveModal('import')}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-medium flex items-center justify-center gap-2 shadow-sm"
        >
          <DocumentArrowUpIcon className="h-5 w-5" />
          JSON importieren
        </button>
      </div>

      {/* Export Modal */}
      <ModalShell
        open={activeModal === 'export'}
        onClose={(_reason: ModalCloseReason) => setActiveModal(null)}
        variant="form"
        size="md"
        icon={CircleStackIcon}
        title="Datenbank-Export"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: () => setActiveModal(null) }}
            primary={{
              label: isLoading ? 'Exportiere...' : 'Jetzt exportieren',
              onClick: handleExport,
              disabled: isLoading,
              loading: isLoading,
              icon: <ArrowDownTrayIcon className="h-5 w-5" />
            }}
          />
        }
      >
        <ModalSection>
          <div className="space-y-4">
            <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4">
              <div className="flex gap-3">
                <InformationCircleIcon className="h-5 w-5 text-info-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-info-800 dark:text-info-200">
                  <p className="font-semibold mb-2">Was wird exportiert:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Alle Mitglieder und deren Daten</li>
                    <li>Hundepässe, Dokumente und Versicherungen</li>
                    <li>Dokumente und Dateien</li>
                    <li>Kalendereinträge und Terminbuchungen</li>
                    <li>Finanzdaten, Konten und Buchungen</li>
                    <li>Gästekarten und Buchungen</li>
                    <li>Vereinskonfiguration und Beiträge</li>
                    <li>Datenschutz-Einwilligungen</li>
                    <li>Audit-Logs und Änderungsprotokolle</li>
                  </ul>
                  <p className="mt-2">
                    <strong>Format:</strong> JSON (editierbar)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-semibold">Sicherheitshinweis:</p>
                  <p>Das Backup enthält sensible Daten. Bewahren Sie es sicher auf und geben Sie es nicht an Unbefugte weiter.</p>
                </div>
              </div>
            </div>
          </div>
        </ModalSection>
      </ModalShell>

      {/* Import Modal */}
      <ModalShell
        open={activeModal === 'import'}
        onClose={(_reason: ModalCloseReason) => {
          setActiveModal(null);
          setSelectedFile(null);
          setBackupData(null);
        }}
        variant="form"
        size="md"
        icon={CircleStackIcon}
        title="Datenbank-Import"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{
              label: 'Abbrechen',
              onClick: () => {
                setActiveModal(null);
                setSelectedFile(null);
                setBackupData(null);
              }
            }}
            primary={{
              label: isLoading ? 'Importiere...' : 'Backup importieren',
              onClick: handleImport,
              disabled: !backupData || isLoading,
              loading: isLoading,
              icon: <ArrowUpTrayIcon className="h-5 w-5" />
            }}
          />
        }
      >
        <ModalSection>
          <div className="space-y-4">
            <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
              <div className="flex gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-error-800 dark:text-error-200">
                  <p className="font-semibold">Warnung:</p>
                  <p>Der Import überschreibt die vorhandenen Daten in der Datenbank. Erstellen Sie vorher ein Backup!</p>
                </div>
              </div>
            </div>

            {/* File selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Backup-Datei auswählen
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 focus:outline-none"
              />
            </div>

            {/* File info */}
            {backupData && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-success-600" />
                  Backup-Details
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p><span className="font-medium">Erstellt am:</span> {new Date(backupData.timestamp).toLocaleString('de-DE')}</p>
                  <p><span className="font-medium">Version:</span> {backupData.version}</p>
                  <p><span className="font-medium">Tabellen:</span> {backupData.metadata.includedTables.length}</p>
                  <p><span className="font-medium">Datensätze:</span> {backupData.metadata.totalRecords}</p>
                  {backupData.metadata.exportedBy && (
                    <p><span className="font-medium">Exportiert von:</span> {backupData.metadata.exportedBy}</p>
                  )}
                </div>
              </div>
            )}

            {/* Options */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white">Import-Optionen</h4>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepMembers}
                  onChange={(e) => setKeepMembers(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Mitglieder-Tabelle behalten
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Die vorhandenen Mitglieder bleiben erhalten und werden nicht überschrieben
                  </p>
                </div>
              </label>

              <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-3">
                <div className="flex gap-2">
                  <InformationCircleIcon className="h-4 w-4 text-info-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-info-800 dark:text-info-200">
                    Auto-Increment IDs werden automatisch zurückgesetzt
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ModalSection>
      </ModalShell>

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={confirmModal.onClose}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.buttons}
        button={confirmModal.button}
      />
    </>
  );
};

export default DatabaseBackup;
